// Unit conversion maps
const conversions = {
  // Volume
  'cup': { metric: 'ml', factor: 236.588 },
  'cups': { metric: 'ml', factor: 236.588 },
  'tbsp': { metric: 'ml', factor: 14.787 },
  'tablespoon': { metric: 'ml', factor: 14.787 },
  'tablespoons': { metric: 'ml', factor: 14.787 },
  'tsp': { metric: 'ml', factor: 4.929 },
  'teaspoon': { metric: 'ml', factor: 4.929 },
  'teaspoons': { metric: 'ml', factor: 4.929 },
  'fl oz': { metric: 'ml', factor: 29.574 },
  'fluid ounce': { metric: 'ml', factor: 29.574 },
  'fluid ounces': { metric: 'ml', factor: 29.574 },
  'pint': { metric: 'ml', factor: 473.176 },
  'pints': { metric: 'ml', factor: 473.176 },
  'quart': { metric: 'l', factor: 0.946 },
  'quarts': { metric: 'l', factor: 0.946 },
  'gallon': { metric: 'l', factor: 3.785 },
  'gallons': { metric: 'l', factor: 3.785 },

  // Weight
  'oz': { metric: 'g', factor: 28.3495 },
  'ounce': { metric: 'g', factor: 28.3495 },
  'ounces': { metric: 'g', factor: 28.3495 },
  'lb': { metric: 'g', factor: 453.592 },
  'lbs': { metric: 'g', factor: 453.592 },
  'pound': { metric: 'g', factor: 453.592 },
  'pounds': { metric: 'g', factor: 453.592 },

  // Temperature (keys lowercase: lookups happen after toLowerCase())
  '°f': { metric: '°C', convert: (f) => ((f - 32) * 5 / 9).toFixed(0) },
  '°c': { metric: '°F', convert: (c) => ((c * 9 / 5) + 32).toFixed(0) },
};

// Metric to US conversions (reverse)
const metricToUS = {
  'ml': { us: 'fl oz', factor: 1 / 29.574 },
  'l': { us: 'quart', factor: 1 / 0.946 },
  'g': { us: 'oz', factor: 1 / 28.3495 },
  'kg': { us: 'lb', factor: 2.205 },
};

export function convertUnit(amount, fromUnit, toSystem = 'metric') {
  const unit = fromUnit.toLowerCase().trim();

  if (toSystem === 'metric') {
    const conv = conversions[unit];
    if (!conv) return { amount, unit: fromUnit };
    if (conv.convert) {
      return { amount: conv.convert(amount), unit: conv.metric };
    }
    let converted = amount * conv.factor;
    let targetUnit = conv.metric;

    // Auto-scale: ml > 1000 → liters, g > 1000 → kg
    if (targetUnit === 'ml' && converted >= 1000) {
      converted = converted / 1000;
      targetUnit = 'l';
    }
    if (targetUnit === 'g' && converted >= 1000) {
      converted = converted / 1000;
      targetUnit = 'kg';
    }

    return { amount: Math.round(converted * 10) / 10, unit: targetUnit };
  } else {
    const conv = metricToUS[unit];
    if (!conv) return { amount, unit: fromUnit };
    return { amount: Math.round(amount * conv.factor * 10) / 10, unit: conv.us };
  }
}

// Single-glyph fractions, keyed by value. Denominators are the ones cooking
// actually uses -- halves, thirds, quarters, sixths, eighths. Fifths are
// deliberately absent: ⅕ and ⅖ exist as characters but no recipe asks for
// two-fifths of a cup, and including them makes 0.2 render as "⅕" where
// "0.2" is the more honest thing to show.
//
// Sixths matter more than they look: halving ⅓ gives ⅙, so the scaling
// toggle produces them constantly.
const DISPLAY_FRACTIONS = [
  [1 / 8, '⅛'], [1 / 6, '⅙'], [1 / 4, '¼'], [1 / 3, '⅓'], [3 / 8, '⅜'],
  [1 / 2, '½'], [5 / 8, '⅝'], [2 / 3, '⅔'], [3 / 4, '¾'], [5 / 6, '⅚'],
  [7 / 8, '⅞']
];

// How close a decimal must be to a fraction before we claim it IS that
// fraction. 0.02 rather than the 0.05 this used to use: at 0.05, 0.2 rendered
// as "¼" -- a 25% overstatement of an ingredient, from a tolerance wide
// enough to swallow a real difference. Tight enough now that a snap means
// the number really was that fraction, loose enough to absorb float drift
// from scaling (⅓ × 2 = 0.6666… must still read as ⅔).
const FRACTION_TOLERANCE = 0.02;

export function formatAmount(amount) {
  if (!Number.isFinite(amount)) return '';
  if (amount === Math.floor(amount)) return amount.toString();

  const whole = Math.floor(amount);
  const decimal = amount - whole;

  // Nearest fraction wins, not the first one within tolerance -- with sixths
  // and eighths both present, "first match" would depend on array order.
  let best = null;
  for (const [value, symbol] of DISPLAY_FRACTIONS) {
    const delta = Math.abs(decimal - value);
    if (delta <= FRACTION_TOLERANCE && (!best || delta < best.delta)) {
      best = { symbol, delta };
    }
  }
  if (best) return whole > 0 ? `${whole} ${best.symbol}` : best.symbol;

  // A decimal that isn't a recognisable fraction. Below 0.1, one decimal
  // place rounds 0.0625 to "0.1" -- a 60% error -- so give small amounts a
  // second place. Everything else stays at one, which is the precision a
  // kitchen can actually act on.
  return amount < 0.1 ? amount.toFixed(2) : amount.toFixed(1);
}

// Vulgar fractions we can READ, which is a wider set than the ones we write:
// a recipe pasted in from elsewhere may well carry ⅕ or ⅐ even though we'd
// never render them.
const VULGAR_FRACTIONS = {
  '½': 1 / 2, '⅓': 1 / 3, '⅔': 2 / 3, '¼': 1 / 4, '¾': 3 / 4,
  '⅕': 1 / 5, '⅖': 2 / 5, '⅗': 3 / 5, '⅘': 4 / 5,
  '⅙': 1 / 6, '⅚': 5 / 6, '⅐': 1 / 7, '⅛': 1 / 8, '⅜': 3 / 8,
  '⅝': 5 / 8, '⅞': 7 / 8, '⅑': 1 / 9, '⅒': 1 / 10
};

const VULGAR_RE = new RegExp(`[${Object.keys(VULGAR_FRACTIONS).join('')}]`, 'g');

/**
 * Read an ingredient amount the way a person writes one.
 *
 * Amounts are stored as free text, and the old code ran parseFloat over them
 * directly. parseFloat('1/2') is 1 -- so a recipe calling for half a cup
 * displayed, converted and (now) scaled as a whole cup. Silently doubling an
 * ingredient is a real defect in a recipe app, not a formatting nitpick.
 *
 * Handles: 2 · 0.5 · .5 · 1/2 · 1 1/2 · 1-1/2 · ½ · 1½ · 1 ½
 * Returns null for anything non-numeric ('to taste', 'a few', '') so callers
 * can pass the original text through untouched.
 */
export function parseAmount(input) {
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;

  let s = String(input ?? '').trim();
  if (!s) return null;
  s = s.replace(/⁄/g, '/'); // U+2044 FRACTION SLASH -> ordinary solidus

  // Peel off any vulgar fractions first, so '1½' and '1 ½' both work.
  let vulgar = 0;
  let sawVulgar = false;
  s = s.replace(VULGAR_RE, (ch) => {
    vulgar += VULGAR_FRACTIONS[ch];
    sawVulgar = true;
    return ' ';
  }).trim();

  if (sawVulgar && !s) return vulgar;

  // Mixed number: '1 1/2' or '1-1/2'. Checked before the range split in
  // scaleAmount, which is why '1-1/2' never reads as "1 to 1/2".
  let m = s.match(/^(\d+)\s*[-\s]\s*(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const den = Number(m[3]);
    if (den === 0) return null;
    return Number(m[1]) + Number(m[2]) / den + vulgar;
  }

  // Simple fraction: '1/2'
  m = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const den = Number(m[2]);
    if (den === 0) return null;
    return Number(m[1]) / den + vulgar;
  }

  // Plain number: '2', '0.5', '.5'
  m = s.match(/^\d*\.?\d+$/);
  if (m) return Number(s) + vulgar;

  return sawVulgar ? vulgar : null;
}

/**
 * Scale an amount string by a factor, preserving how it was written.
 *
 * Ranges are scaled at both ends ('1-2' doubled is '2-4'), because dropping
 * to a single number would quietly discard the author's stated flexibility.
 * Anything unparseable ('to taste', 'a splash') passes through unchanged --
 * scaling nonsense into more nonsense helps nobody.
 */
export function scaleAmount(text, factor = 1) {
  const raw = String(text ?? '').trim();
  if (!raw || factor === 1) return raw;

  // Whole-string parse FIRST: '1-1/2' is one and a half, not a range.
  const single = parseAmount(raw);
  if (single !== null) return formatAmount(single * factor);

  // '1-2', '1 – 2', '1 to 2'
  const range = raw.match(/^(.+?)\s*(?:-|–|—|\bto\b)\s*(.+)$/i);
  if (range) {
    const lo = parseAmount(range[1]);
    const hi = parseAmount(range[2]);
    if (lo !== null && hi !== null) {
      return `${formatAmount(lo * factor)}–${formatAmount(hi * factor)}`;
    }
  }
  return raw;
}

// The scale options offered on a recipe. Thirds and quarters were left out
// deliberately: they turn most amounts into decimals nobody can measure,
// whereas halving and doubling land on fractions a kitchen already owns.
export const SCALE_OPTIONS = [
  { factor: 0.5, label: '½×' },
  { factor: 1, label: '1×' },
  { factor: 2, label: '2×' },
  { factor: 3, label: '3×' }
];

export const DIETARY_TAGS = [
  'vegetarian',
  'vegan',
  'gluten-free',
  'dairy-free',
  'paleo',
  'keto',
  'carnivore',
  'low-carb',
  'high-protein',
  'nut-free',
  'soy-free',
  'whole30',
  'pescatarian'
];

// Major food allergens (FDA Big 9 + EU-regulated + common exclusions), as
// "no X" preference/filter tags. These are PREFERENCE tags only (profile
// allergies, browse filters, AI Chef constraints) -- recipes are no longer
// tagged by their authors. Instead the server's enrichment scan writes
// recipe.allergens, an array of the allergen ids it detected (server-only
// field; [] means scanned-and-clean). Each tag here is 'no ' + an allergen
// id, matching ALLERGEN_IDS in functions/pure.js (enforced by a test); a
// vocabulary change there needs an ALLERGENS_VERSION bump to rescan.
export const ALLERGY_TAGS = [
  'no peanuts',
  'no tree nuts',
  'no dairy',
  'no eggs',
  'no gluten',
  'no soy',
  'no fish',
  'no shellfish',
  'no sesame',
  'no mustard',
  'no celery',
  'no sulfites',
  'no lupin',
  'no molluscs',
  'no corn',
  'no coconut',
  'no buckwheat',
  'no garlic',
  'no onion',
  'no nightshades'
];

const ALLERGY_TAG_SET = new Set(ALLERGY_TAGS);

export const isAllergyTag = (tag) => ALLERGY_TAG_SET.has(tag);

// 'no peanuts' -> 'peanuts' (the id the server's scan writes).
export const allergenFromTag = (tag) => tag.replace(/^no /, '');

// True when a recipe is safe to show someone with the given allergy tags
// selected. Scanned recipes (allergens array present) are judged by the scan
// alone -- it supersedes any author claim. Unscanned recipes (older data the
// backfill hasn't reached) fall back to the author's explicit legacy
// "no X" tag, and are otherwise EXCLUDED: for allergies, "don't know" must
// mean "don't show".
export function matchesAllergyFilters(recipe, allergyTags) {
  if (!allergyTags || allergyTags.length === 0) return true;
  const scanned = Array.isArray(recipe.allergens);
  return allergyTags.every(tag =>
    scanned
      ? !recipe.allergens.includes(allergenFromTag(tag))
      : (recipe.dietaryTags || []).includes(tag)
  );
}

// Profile visibility, mirrored from VISIBILITY_LEVELS in functions/pure.js.
// The 'private' level (labelled "Unlisted" in the UI) hides the person, not
// their cooking: off the open web and out of search, while recipes stay
// web-readable. It is NOT hidden from other signed-in members -- Firestore
// list queries don't enforce the in-document visibility check, so the copy
// promises only what the rules actually deliver. See the KNOWN LIMITATION
// test in tests/rules/firestore.test.js and S2 in todo.txt.
export const VISIBILITY_OPTIONS = [
  {
    id: 'public',
    label: 'Public',
    hint: 'Anyone on the web can see your profile and recipes, and find you in search.'
  },
  {
    id: 'users',
    label: 'Tastr users only',
    // Recipes stay web-readable at this level so shared links keep working;
    // it is the PROFILE page that goes members-only. Saying "nothing is
    // visible to the open web" here would be a promise the rules don't keep
    // (see recipeIsPubliclyVisible in functions/pure.js).
    hint: 'Your profile page is visible only to signed-in Tastr users. Recipes you publish stay viewable by anyone with the link, credited to your name.'
  },
  {
    id: 'private',
    // Wording is deliberately limited to what the rules actually enforce.
    // A signed-in member can still look up profiles, so this must NOT
    // promise to hide you from other Tastr users.
    label: 'Unlisted',
    hint: 'Your profile is hidden from the open web and you won\'t appear in search. Your recipes are visible only to signed-in Tastr users, so links you share won\'t open for anyone else. Other signed-in members may still be able to look you up.'
  }
];

// Heuristic for a display name that was never chosen by a person -- test
// accounts and bots typically keep whatever random token they were created
// with ("NQmaRRuZGyEoESRw"). Used to keep the Chefs directory clean; it
// never deletes or blocks anything, and admins still see these accounts so
// they can review them.
//
// Deliberately conservative -- a false positive hides a real person from
// discovery, which is worse than letting one bot through:
//   - anything containing a space is left alone (real names have spaces)
//   - anything under 12 characters is left alone (short handles are normal)
//   - the actual signal is erratic capitalisation: random tokens flip case
//     repeatedly, while "nateanderson" or "McDonald" do not.
export function looksAutogeneratedName(name) {
  const n = (name || '').trim();
  if (!n || /\s/.test(n)) return false;
  if (n.length < 12) return false;
  const isUpper = (c) => c >= 'A' && c <= 'Z';
  let flips = 0;
  for (let i = 1; i < n.length; i++) {
    if (/[a-zA-Z]/.test(n[i]) && /[a-zA-Z]/.test(n[i - 1])
        && isUpper(n[i]) !== isUpper(n[i - 1])) flips++;
  }
  return flips >= 5;
}

// What a NEW signup gets. Public by default so a shared recipe previews
// properly and the profile is reachable -- the whole growth loop depends on
// it, and an opt-in default left it dormant for every account but the
// founder's. Disclosed at signup and changeable any time on /profile.
export const DEFAULT_VISIBILITY = 'public';

// What a profile written BEFORE the visibility field existed means. This is
// deliberately NOT the same constant as DEFAULT_VISIBILITY: those accounts
// never agreed to anything, so raising the signup default must never
// retroactively expose them. Keep this 'users' unless every existing doc has
// been migrated AND their owners have consented.
export const LEGACY_VISIBILITY = 'users';

// Back-compat for profiles written before the three-level model (mirrors
// deriveVisibility in functions/pure.js).
export function visibilityOf(profile) {
  const v = profile && profile.visibility;
  if (VISIBILITY_OPTIONS.some(o => o.id === v)) return v;
  return profile && profile.isPublic === true ? 'public' : LEGACY_VISIBILITY;
}

export const CUISINE_TAGS = [
  'american',
  'italian',
  'mexican',
  'chinese',
  'japanese',
  'indian',
  'thai',
  'french',
  'mediterranean',
  'korean',
  'middle eastern',
  'african',
  'caribbean',
  'southern',
  'cajun'
];

export const MEAL_TYPES = [
  'breakfast',
  'lunch',
  'dinner',
  'snack',
  'dessert',
  'appetizer',
  'side dish',
  'beverage'
];

export const DIFFICULTY_LEVELS = ['easy', 'moderate', 'difficult'];

export const TIME_FILTERS = [
  { id: 'under30', label: 'under 30 min' },
  { id: '30to60', label: '30–60 min' },
  { id: 'over60', label: 'over 60 min' }
];

export function getTotalTime(recipe) {
  return (recipe.prepTime || 0) + (recipe.cookTime || 0);
}

// Difficulty is derived, not stored: estimated from step count, ingredient
// count, and total time so it works for every recipe without a schema change.
export function getDifficulty(recipe) {
  const steps = recipe.steps?.length || 0;
  const ingredients = recipe.ingredients?.length || 0;
  const totalTime = getTotalTime(recipe);

  if (steps > 10 || ingredients > 12 || totalTime > 90) return 'difficult';
  if (steps <= 6 && ingredients <= 8 && totalTime <= 35) return 'easy';
  return 'moderate';
}

// When a recipe was posted, in millis, or null when it can't be determined.
//
// createdAt arrives in four shapes depending on where the object came from:
// a Firestore Timestamp (live read), a plain {seconds} (some cached paths),
// a Date (optimistic local rows), or an ISO string. Returns null rather than
// a fallback so callers decide what "unknown" means -- a filter must exclude
// it, a display must omit it.
//
// NB rankCurated in ranking.js has its own version that falls back to NOW,
// deliberately: an undated recipe should rank as fresh, but must not appear
// in a "posted this week" filter. Same input, opposite correct answer, so
// they stay separate.
export function recipeCreatedMillis(recipe) {
  const c = recipe && (recipe.createdAt || recipe.publishedAt);
  if (!c) return null;
  if (typeof c.toMillis === 'function') return c.toMillis();
  if (typeof c.toDate === 'function') return c.toDate().getTime();
  if (c instanceof Date) return c.getTime();
  if (typeof c.seconds === 'number') return c.seconds * 1000;
  const parsed = new Date(c).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

// Human-readable posting date. Deliberately absolute ("17 Aug 2026") rather
// than relative ("3 days ago"): a recipe is a reference you come back to, and
// "2 years ago" tells you less than the year does when you're deciding
// whether a technique or an ingredient list is still current.
export function formatPostedDate(recipe, locale = undefined) {
  const millis = recipeCreatedMillis(recipe);
  if (millis === null) return '';
  return new Date(millis).toLocaleDateString(locale, {
    year: 'numeric', month: 'short', day: 'numeric'
  });
}

// Browse filter: how recently a recipe was posted. Ranges are open-ended at
// the recent end (a recipe from today matches 'week', 'month' and 'year'),
// which is what "posted within" means to a reader.
export const DATE_FILTERS = [
  { id: 'week', label: 'past week', days: 7 },
  { id: 'month', label: 'past month', days: 30 },
  { id: 'year', label: 'past year', days: 365 }
];

export function matchesDateFilter(recipe, filterId, now = Date.now()) {
  if (!filterId) return true;
  const filter = DATE_FILTERS.find(f => f.id === filterId);
  if (!filter) return true;
  const millis = recipeCreatedMillis(recipe);
  // No date means we cannot honour the filter. Exclude rather than include:
  // showing an undated recipe under "posted this week" is a claim we can't
  // support, and the same call the allergy filters make for the same reason.
  if (millis === null) return false;
  return now - millis <= filter.days * 24 * 60 * 60 * 1000;
}

export function matchesTimeFilter(recipe, filterId) {
  if (!filterId) return true;
  const total = getTotalTime(recipe);
  if (total === 0) return false; // no time info -- can't claim a range
  if (filterId === 'under30') return total < 30;
  if (filterId === '30to60') return total >= 30 && total <= 60;
  return total > 60;
}

// Ingredient units, grouped for the dropdown -- a flat list of thirty-odd
// entries is a scroll, whereas groups let you jump straight to the right kind
// of measure.
//
// NOTHING MAY BE REMOVED FROM THIS LIST. Units are stored as plain strings on
// every recipe ever written; deleting one orphans the recipes using it, and
// the editor would silently rewrite the unit on the next save. Adding is
// always safe, removing never is.
//
// 'fl oz' is new here but was already in the conversions map above -- it
// could be converted but never chosen, because it was missing from this list.
export const UNIT_GROUPS = [
  {
    label: 'Volume',
    units: ['tsp', 'tbsp', 'fl oz', 'cups', 'pint', 'quart', 'gallon', 'ml', 'l']
  },
  {
    label: 'Weight',
    units: ['oz', 'lbs', 'g', 'kg']
  },
  {
    // Count units have no metric equivalent, which is fine: convertUnit
    // returns anything it doesn't recognise untouched, so "3 sprigs" stays
    // "3 sprigs" when someone flips to metric.
    label: 'Count',
    units: [
      'whole', 'pieces', 'halves', 'slices', 'strips', 'wedges', 'cubes',
      'cloves', 'sprigs', 'leaves', 'stalks', 'bunch', 'head', 'bulb',
      'ears', 'fillets', 'sticks', 'cans', 'jars', 'packages', 'sheets'
    ]
  },
  {
    label: 'Small amounts',
    units: ['pinch', 'dash', 'drop', 'splash', 'handful', 'to taste']
  }
];

export const UNITS = UNIT_GROUPS.flatMap(g => g.units);

// How a linked recipe relates to the one linking it. Stored per link on
// recipe.linkedRecipes as { recipeId, relation }; the author picks which.
// 'component' is a part of this recipe (the cream sauce a chili calls for),
// 'related' is a suggested pairing (cornbread to serve alongside).
// NB: the ids are persisted on recipe.linkedRecipes, so 'component' stays as
// the id even though its wording changed -- renaming it would orphan every
// existing link.
export const LINK_RELATIONS = [
  { id: 'component', label: 'Completes this dish', heading: 'Completes this dish' },
  { id: 'related', label: 'Goes well with', heading: 'Goes well with' }
];

export const DEFAULT_LINK_RELATION = 'component';
